import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { faCircleArrowLeft, faHomeAlt, faPlusCircle, faPenToSquare, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FinancialService } from '../_services/financial.service';
import { MaintenanceRateRecord } from '../_models/maintenance_rate';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-manage-rates',
    templateUrl: './manage-rates.component.html',
    styleUrls: ['./manage-rates.component.css'],
    standalone: false
})
export class ManageRatesComponent implements OnInit {

  faCircleArrowLeft = faCircleArrowLeft;
  faHomeAlt = faHomeAlt;
  faPlusCircle = faPlusCircle;
  faPenToSquare = faPenToSquare;
  faCheck = faCheck;
  faTimes = faTimes;

  mode: 'list' | 'form' | 'view' = 'list';

  loading = false;
  saving = false;
  errorMessage = '';

  rates: MaintenanceRateRecord[] = [];
  selectedRate: MaintenanceRateRecord | null = null;
  editingRate: MaintenanceRateRecord | null = null;

  rateForm!: FormGroup;

  property: any;

  constructor(private router: Router,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private financialService: FinancialService) { }

  ngOnInit(): void {
    let propertystr = localStorage.getItem('property');
    this.property = JSON.parse(propertystr ? propertystr : '{}');

    this.rateForm = this.formBuilder.group({
      unit_entitlement: ['', [Validators.required, Validators.min(1)]],
      start_period: ['', Validators.required],
      end_period: ['', Validators.required],
      amount: ['', [Validators.required, Validators.min(0.01)]]
    });

    this.loadRates();
  }

  get f() { return this.rateForm.controls; }

  get sortedRates(): MaintenanceRateRecord[] {
    return [...this.rates].sort((a, b) => new Date(b.start_period).getTime() - new Date(a.start_period).getTime());
  }

  loadRates(): void {
    if (!this.property?.id) {
      this.errorMessage = 'Valid property is required to manage rates.';
      this.rates = [];
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.financialService.fetchRatesAtProperty(this.property.id).subscribe({
      next: (data) => {
        this.rates = Array.isArray(data) ? data : [];
        this.loading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load rates.';
        this.rates = [];
        this.loading = false;
        if (!environment.production) {
          console.log('Fetch rates: failure response: ', error);
        }
      }
    });
  }

  resetForm(): void {
    this.editingRate = null;
    this.rateForm.reset();
    this.errorMessage = '';
  }

  startCreate(): void {
    this.resetForm();
    this.mode = 'form';
  }

  startEdit(rate: MaintenanceRateRecord): void {
    this.editingRate = rate;
    this.rateForm.setValue({
      unit_entitlement: rate.unit_entitlement,
      start_period: rate.start_period ? rate.start_period.slice(0, 10) : '',
      end_period: rate.end_period ? rate.end_period.slice(0, 10) : '',
      amount: rate.amount
    });
    this.errorMessage = '';
    this.mode = 'form';
  }

  viewRate(rate: MaintenanceRateRecord): void {
    this.selectedRate = rate;
    this.mode = 'view';
  }

  cancelForm(): void {
    this.resetForm();
    this.mode = 'list';
  }

  closeView(): void {
    this.selectedRate = null;
    this.mode = 'list';
  }

  saveRate(): void {
    if (this.rateForm.invalid) {
      return;
    }

    if (!this.property?.id) {
      this.errorMessage = 'Valid property is required to manage rates.';
      return;
    }

    const formdata = this.rateForm.value;

    if (new Date(formdata.end_period) < new Date(formdata.start_period)) {
      this.errorMessage = 'End date must be on or after the start date.';
      return;
    }

    const payload = {
      propertyId: this.property.id,
      unit_entitlement: Number(formdata.unit_entitlement),
      start_period: formdata.start_period,
      end_period: formdata.end_period,
      amount: Number(formdata.amount)
    };

    this.saving = true;
    this.errorMessage = '';

    const request$ = this.editingRate
      ? this.financialService.updateMaintenanceRate(this.property.id, this.editingRate.id, payload)
      : this.financialService.createMaintenanceRate(payload);

    request$.subscribe({
      next: (resp) => {
        this.saving = false;
        this.toastr.success(resp.message ?? 'Rate saved successfully.', 'Success');
        this.resetForm();
        this.mode = 'list';
        this.loadRates();
      },
      error: (error) => {
        this.saving = false;
        this.errorMessage = 'Failed to save rate.';
        if (!environment.production) {
          console.log('Save rate: failure response: ', error);
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/manage-property-menu']);
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }
}
